import { useRef, useState } from 'react';
import { Button } from '@givebutter/primitives/button';
import { Label } from '@givebutter/primitives/label';
import { faChevronDown, faPenToSquare } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import * as Drawer from 'components/drawer';
import * as DropdownMenu from 'components/dropdown-menu';
import { FilterGroupProvider } from 'components/filter/filters-next/FilterGroupContext';
import { QuickFiltersBar } from 'components/filter/filters-next/QuickFiltersBar';
import * as Input from 'components/input';
import { AddTaskDialog } from 'components/tasks/AddTaskDialog';
import { useTasksFilterStore } from 'components/tasks/utils/useTasksFilterStore';
import { IntercomProductTourTargets } from 'constants/intercom';
import { twJoin } from 'tailwind-merge';

const TasksHeader = () => {
    const { query, setQuery, sortBy, setSortBy, sortByDesc, setSortByDesc } = useTasksFilterStore(
        ({ query, setQuery, sortBy, setSortBy, sortByDesc, setSortByDesc }) => ({
            query,
            setQuery,
            sortBy,
            setSortBy,
            sortByDesc,
            setSortByDesc,
        }),
    );

    const [searchVisible, setSearchVisible] = useState(!!query);
    const searchInputRef = useRef<HTMLInputElement>(null);

    return (
        <div className="flex flex-col gap-3 border-b border-gray-200 px-8 py-6">
            <div className="flex items-center justify-between gap-2">
                <Drawer.Title
                    data-intercom-target={IntercomProductTourTargets.TasksHeader}
                    className="text-2xl font-medium"
                >
                    Tasks
                </Drawer.Title>
                <div className="flex flex-row-reverse items-center">
                    <AddTaskDialog>
                        <Button
                            variant="transparent"
                            shape={'square'}
                            className="hover:!bg-gray-100"
                            size="sm"
                            aria-label="Add task"
                        >
                            <FontAwesomeIcon icon={faPenToSquare} className="text-gray-500" />
                        </Button>
                    </AddTaskDialog>
                    <Label className="sr-only" htmlFor="tasks_search">
                        Search
                    </Label>
                    <Input.Root
                        size={'sm'}
                        className={twJoin(
                            'transition-all',
                            searchVisible
                                ? 'w-60'
                                : 'size-8 cursor-pointer items-center justify-center ring-0 hover:bg-gray-100',
                        )}
                    >
                        <Input.SearchIcon className={twJoin(!searchVisible && 'text-gray-500')} />
                        <Input.Form
                            id="tasks_search"
                            ref={searchInputRef}
                            data-intercom-target={IntercomProductTourTargets.TasksSearch}
                            value={query}
                            placeholder={'Search'}
                            className={twJoin(searchVisible ? '' : 'sr-only')}
                            onChange={e => setQuery(e.target.value)}
                            onBlur={() => {
                                if (query) return;
                                setSearchVisible(false);
                            }}
                            onFocus={() => {
                                setSearchVisible(true);
                            }}
                        />
                    </Input.Root>
                </div>
            </div>
            <div className="flex items-start justify-between gap-4">
                <div className="flex flex-wrap items-start gap-2">
                    <FilterGroupProvider showInnerOperator={false} showOuterOperator={false}>
                        <QuickFiltersBar
                            quickFilters={[
                                'assigned_to',
                                'starred',
                                'due_at',
                                'created_by',
                                'archived',
                            ]}
                        />
                    </FilterGroupProvider>
                </div>
                <DropdownMenu.Root>
                    <DropdownMenu.Trigger className="flex items-center gap-1 text-sm text-gray-500">
                        Sort
                        <FontAwesomeIcon icon={faChevronDown} className="text-gray-400" size="sm" />
                    </DropdownMenu.Trigger>
                    <DropdownMenu.Content align="end">
                        <DropdownMenu.RadioGroup
                            value={sortByDesc || sortBy || undefined}
                            onValueChange={value => {
                                if (value === 'created_at') {
                                    setSortByDesc('created_at');
                                    setSortBy(undefined);
                                } else {
                                    setSortBy('due_at');
                                    setSortByDesc(undefined);
                                }
                            }}
                        >
                            <DropdownMenu.RadioItem value={'created_at'}>
                                Most recent created date
                            </DropdownMenu.RadioItem>
                            <DropdownMenu.RadioItem value={'due_at'}>
                                Most recent due date
                            </DropdownMenu.RadioItem>
                        </DropdownMenu.RadioGroup>
                    </DropdownMenu.Content>
                </DropdownMenu.Root>
            </div>
        </div>
    );
};

export { TasksHeader };
